// ABOUTME: Pure helpers for the per-user preferences document (users/{uid}/settings/prefs).
// ABOUTME: Shape, validation of untrusted Firestore data, and change detection for write-back.

import type { Spacing } from "../stores/ui";
import { SEARCH_ENGINES } from "./searchEngines";

/** The preferences that follow the account across devices. */
export interface UserPrefs {
  baseColor: string;
  paletteIdx: number;
  columnCount: number;
  spacing: Spacing;
  openLinksInNewTab: boolean;
  searchEngineIdx: number;
  groupingEnabled: boolean;
}

export const PREF_KEYS: ReadonlyArray<keyof UserPrefs> = [
  "baseColor",
  "paletteIdx",
  "columnCount",
  "spacing",
  "openLinksInNewTab",
  "searchEngineIdx",
  "groupingEnabled",
];

const SPACINGS: ReadonlyArray<Spacing> = ["compact", "default", "relaxed"];

// Mirrors the caps in firestore.rules `isValidPrefs` — keep the two in step.
const MAX_STRING = 32;
const MAX_PALETTE_IDX = 50;
const MAX_COLUMNS = 10;

/** Picks just the synced preference fields out of a larger object (e.g. the UI store). */
export function pickPrefs(source: UserPrefs): UserPrefs {
  const out: Partial<UserPrefs> = {};
  for (const key of PREF_KEYS) {
    (out as Record<string, unknown>)[key] = source[key];
  }
  return out as UserPrefs;
}

/**
 * Keeps only the fields of a Firestore document that are present and valid.
 * The rules reject bad writes, but data can predate a rule or arrive through
 * the console, and one bad value must not take the whole theme down.
 */
export function sanitizePrefs(raw: unknown): Partial<UserPrefs> {
  if (!raw || typeof raw !== "object") return {};
  const d = raw as Record<string, unknown>;
  const out: Partial<UserPrefs> = {};

  if (typeof d.baseColor === "string" && d.baseColor.length <= MAX_STRING) {
    out.baseColor = d.baseColor;
  }
  if (
    Number.isInteger(d.paletteIdx) &&
    (d.paletteIdx as number) >= 0 &&
    (d.paletteIdx as number) <= MAX_PALETTE_IDX
  ) {
    out.paletteIdx = d.paletteIdx as number;
  }
  if (
    Number.isInteger(d.columnCount) &&
    (d.columnCount as number) >= 1 &&
    (d.columnCount as number) <= MAX_COLUMNS
  ) {
    out.columnCount = d.columnCount as number;
  }
  if (typeof d.spacing === "string" && (SPACINGS as ReadonlyArray<string>).includes(d.spacing)) {
    out.spacing = d.spacing as Spacing;
  }
  if (typeof d.openLinksInNewTab === "boolean") {
    out.openLinksInNewTab = d.openLinksInNewTab;
  }
  if (
    Number.isInteger(d.searchEngineIdx) &&
    (d.searchEngineIdx as number) >= 0 &&
    (d.searchEngineIdx as number) < SEARCH_ENGINES.length
  ) {
    out.searchEngineIdx = d.searchEngineIdx as number;
  }
  if (typeof d.groupingEnabled === "boolean") {
    out.groupingEnabled = d.groupingEnabled;
  }
  return out;
}

/** The fields of `next` that differ from `prev`. Empty when nothing changed. */
export function diffPrefs(prev: Partial<UserPrefs>, next: Partial<UserPrefs>): Partial<UserPrefs> {
  const patch: Partial<UserPrefs> = {};
  for (const key of PREF_KEYS) {
    if (next[key] !== undefined && next[key] !== prev[key]) {
      (patch as Record<string, unknown>)[key] = next[key];
    }
  }
  return patch;
}
